import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TablePagination,
    Typography,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    DialogContentText,
    TextField,
    Grid,
    IconButton,
    Snackbar,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Tooltip,
    Stack,
    Avatar,
    useTheme,
    useMediaQuery,
    CircularProgress,
    Collapse,
    FormControlLabel,
    Checkbox,
    InputAdornment
} from '@mui/material';
import { 
    Add as AddIcon, 
    Edit as EditIcon, 
    Delete as DeleteIcon, 
    Key as KeyIcon,
    PersonAdd as PersonAddIcon,
    AdminPanelSettings as AdminIcon,
    SupervisorAccount as CompanyAdminIcon,
    Person as UserIcon,
    Build as BuildIcon,
    Visibility as VisibilityIcon,
    Refresh as RefreshIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    FilterList as FilterListIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import apiClient from '../../api';
import { useAuth } from '../../context/AuthContext';
import TableSkeleton from '../skeleton/TableSkeleton';

function UserManagement() {
    // State for users data
    const [users, setUsers] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Get current user and role
    const { user: currentAuthUser } = useAuth();
    const isAdmin = currentAuthUser?.role === 'admin';
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    // State for dialog
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogType, setDialogType] = useState('add'); // 'add', 'edit', 'delete'
    const [currentUser, setCurrentUser] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        role: 'company_user',
        company: '',
        isActive: true,
        isVideoAdmin: false,
        personnelType: 'driver'
    });
    
    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    
    // Filter state
    const [showFilters, setShowFilters] = useState(false);
    const [roleFilter, setRoleFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [orderBy, setOrderBy] = useState('name');
    const [order, setOrder] = useState('asc');
    
    // Snackbar state
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    // Add state for password reset confirmation
    const [resetPasswordDialog, setResetPasswordDialog] = useState({
        open: false,
        user: null
    });

    // Filter users based on filters
    useEffect(() => {
        let filtered = [...users];
        
        if (roleFilter !== 'all') {
            filtered = filtered.filter(user => user.role === roleFilter);
        }
        
        if (statusFilter !== 'all') {
            const isActive = statusFilter === 'active';
            filtered = filtered.filter(user => user.isActive === isActive);
        }

        const query = searchQuery.trim().toLowerCase();
        if (query) {
            filtered = filtered.filter((user) => {
                const roleLabel = user.role === 'admin'
                    ? 'admin'
                    : user.role === 'company_admin'
                        ? 'company admin'
                        : user.role === 'service_admin'
                            ? 'service admin'
                            : user.role === 'service_viewer'
                                ? 'service'
                                : user.role === 'company_user'
                                    ? 'company user'
                                    : String(user.role || '');
                const personnel = user.personnelType === 'office' ? 'office personnel' : 'driver personnel';
                const haystack = [
                    user.name,
                    user.email,
                    user.company?.name,
                    user.role,
                    roleLabel,
                    personnel,
                    user.isActive ? 'active' : 'inactive',
                    user.isOnline ? 'online' : 'offline'
                ].filter(Boolean).join(' ').toLowerCase();
                return haystack.includes(query);
            });
        }
        
        setFilteredUsers(filtered);
    }, [users, roleFilter, statusFilter, searchQuery]);

    useEffect(() => {
        setPage(0);
    }, [roleFilter, statusFilter, searchQuery, orderBy, order]);

    const roleSortRank = (role) => {
        const ranks = { admin: 0, company_admin: 1, service_admin: 2, service_viewer: 3, company_user: 4 };
        return ranks[role] ?? 3;
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const getComparator = useCallback((key) => {
        return (a, b) => {
            let aVal;
            let bVal;
            switch (key) {
                case 'name':
                    aVal = (a.name || '').toLowerCase();
                    bVal = (b.name || '').toLowerCase();
                    break;
                case 'role':
                    aVal = roleSortRank(a.role);
                    bVal = roleSortRank(b.role);
                    break;
                case 'personnelType':
                    aVal = a.personnelType === 'office' ? 1 : 0;
                    bVal = b.personnelType === 'office' ? 1 : 0;
                    break;
                case 'company':
                    aVal = (a.company?.name || '').toLowerCase();
                    bVal = (b.company?.name || '').toLowerCase();
                    break;
                case 'status':
                    aVal = a.isActive ? 1 : 0;
                    bVal = b.isActive ? 1 : 0;
                    break;
                case 'online':
                    aVal = a.isOnline ? 1 : 0;
                    bVal = b.isOnline ? 1 : 0;
                    break;
                case 'lastLogin': {
                    const aTime = a.lastLogin ? new Date(a.lastLogin).getTime() : 0;
                    const bTime = b.lastLogin ? new Date(b.lastLogin).getTime() : 0;
                    aVal = Number.isFinite(aTime) ? aTime : 0;
                    bVal = Number.isFinite(bTime) ? bTime : 0;
                    break;
                }
                default:
                    aVal = a[key];
                    bVal = b[key];
            }
            if (aVal < bVal) return order === 'asc' ? -1 : 1;
            if (aVal > bVal) return order === 'asc' ? 1 : -1;
            return 0;
        };
    }, [order, order]);

    const sortedFilteredUsers = useMemo(() => {
        const rows = [...filteredUsers];
        rows.sort(getComparator(orderBy));
        return rows;
    }, [filteredUsers, orderBy, order, getComparator]);

    const renderSortableHeader = (id, label, align) => (
        <TableCell
            key={id}
            align={align}
            sortDirection={orderBy === id ? order : false}
            sx={{ fontWeight: 600 }}
        >
            <TableSortLabel
                active={orderBy === id}
                direction={orderBy === id ? order : 'asc'}
                onClick={() => handleRequestSort(id)}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );

    // Fetch users and companies data
    const fetchUsers = async () => {
        console.log('[UserManagement] Fetching fresh users data...', { isAdmin, user: currentAuthUser?.email, role: currentAuthUser?.role });
        setLoading(true);
        try {
            // Add timestamp to prevent caching
            const timestamp = new Date().getTime();
            console.log('[UserManagement] Timestamp:', timestamp);
            
            // Use different API endpoints based on user role
            let usersResponse, companiesResponse;
            
            if (isAdmin) {
                console.log('[UserManagement] Admin user - fetching all users and companies...');
                // Admin can access all users and companies
                [usersResponse, companiesResponse] = await Promise.all([
                    apiClient.get(`/admin/users?_t=${timestamp}`, {
                        headers: {
                            'Cache-Control': 'no-cache',
                            'Pragma': 'no-cache'
                        }
                    }).then(res => {
                        console.log('[UserManagement] Users API response:', { 
                            status: res.status, 
                            data: res.data,
                            hasUsers: !!res.data?.data?.users,
                            userCount: res.data?.data?.users?.length || 0
                        });
                        return res;
                    }).catch(err => {
                        console.error('[UserManagement] Users API error:', { 
                            status: err.response?.status, 
                            message: err.message,
                            data: err.response?.data,
                            config: err.config
                        });
                        throw err;
                    }),
                    apiClient.get(`/admin/companies?_t=${timestamp}`, {
                        headers: {
                            'Cache-Control': 'no-cache',
                            'Pragma': 'no-cache'
                        }
                    }).then(res => {
                        console.log('[UserManagement] Companies API response:', { 
                            status: res.status, 
                            data: res.data,
                            hasCompanies: !!res.data?.data?.companies,
                            companyCount: res.data?.data?.companies?.length || 0
                        });
                        return res;
                    }).catch(err => {
                        console.error('[UserManagement] Companies API error:', { 
                            status: err.response?.status, 
                            message: err.message,
                            data: err.response?.data,
                            config: err.config
                        });
                        throw err;
                    })
                ]);
            } else {
                console.log('[UserManagement] Company admin user - fetching company users...');
                // Company admin can only access users from their company
                [usersResponse, companiesResponse] = await Promise.all([
                    apiClient.get(`/users/company?_t=${timestamp}`, {
                        headers: {
                            'Cache-Control': 'no-cache',
                            'Pragma': 'no-cache'
                        }
                    }),
                    // Company admin only needs their own company info
                    apiClient.get(`/users/company/info?_t=${timestamp}`, {
                        headers: {
                            'Cache-Control': 'no-cache',
                            'Pragma': 'no-cache'
                        }
                    })
                ]);
            }
            
            // Handle different response formats
            let fetchedUsers, fetchedCompanies;
            
            if (isAdmin) {
                // Admin endpoint returns users in data.data.users
                fetchedUsers = usersResponse.data.data?.users || 
                               usersResponse.data.status === 'success' ? usersResponse.data.data?.users : 
                               usersResponse.data?.users || [];
                               
                fetchedCompanies = companiesResponse.data.data?.companies || 
                                 companiesResponse.data.status === 'success' ? companiesResponse.data.data?.companies : 
                                 companiesResponse.data?.companies || [];
            } else {
                // Company endpoint returns users directly in data.users 
                fetchedUsers = usersResponse.data.users || [];
                fetchedCompanies = [companiesResponse.data.company] || [];
            }
            
            console.log(`[UserManagement] Fetched ${fetchedUsers.length} users from API`);
            
            setUsers(fetchedUsers);
            setCompanies(fetchedCompanies);
            setError(null);
        } catch (err) {
            console.error('[UserManagement] Error fetching data:', err);
            console.error('[UserManagement] Error details:', {
                message: err.message,
                status: err.response?.status,
                data: err.response?.data,
                config: err.config,
                stack: err.stack
            });
            setError('Failed to fetch users. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    // Initial data fetch
    useEffect(() => {
        fetchUsers();
    }, [isAdmin]); // Re-fetch when isAdmin changes

    // Handle dialog open/close
    const handleOpenDialog = (type, user = null) => {
        setDialogType(type);
        setCurrentUser(user);
        
        if (type === 'add') {
            // For company admin, pre-select their company and default to company_user role
            setFormData({
                name: '',
                email: '',
                role: 'company_user',
                company: !isAdmin ? (currentAuthUser.company._id || currentAuthUser.company) : '',
                isActive: true,
                isVideoAdmin: false,
                personnelType: 'driver'
            });
        } else if (type === 'edit' && user) {
            setFormData({
                name: user.name || '',
                email: user.email || '',
                role: user.role || 'company_user',
                company: user.company?._id || user.company || '',
                isActive: user.isActive,
                isVideoAdmin: Boolean(user.isVideoAdmin),
                personnelType: user.personnelType === 'office' ? 'office' : 'driver'
            });
        }
        
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    // Add a utility function to format names properly
    const formatName = (name) => {
        if (!name) return '';
        
        // Split by spaces, hyphens, and apostrophes while preserving them
        return name.toLowerCase().replace(/(?:^|\s|-|'|")(\S)/g, match => match.toUpperCase());
    };

    // Handle form submission
    const handleSubmit = async () => {
        try {
            // Skip validation for delete operations
            if (dialogType === 'delete') {
                await apiClient.delete(`/admin/users/${currentUser._id}`);
                setSnackbar({
                    open: true,
                    message: 'User deleted successfully',
                    severity: 'success'
                });
            } else if (dialogType === 'add') {
                // Format the name properly before sending
                const payload = {
                    ...formData,
                    name: formatName(formData.name)
                };
                
                // Handle company ID based on user role
                if (isAdmin) {
                    // Admin can set any company - send the selected company as companyId
                    if (formData.company) {
                        payload.companyId = formData.company;
                    }
                    delete payload.company; // Remove the company field, backend expects companyId
                } else {
                    // Company admin creates users in their own company
                    payload.companyId = currentAuthUser.company._id || currentAuthUser.company;
                    delete payload.company; // Remove the company field, backend expects companyId
                }
                
                await apiClient.post('/admin/users', payload);
                setSnackbar({
                    open: true,
                    message: 'User added successfully. A password setup link has been sent to the user\'s email.',
                    severity: 'success'
                });
            } else if (dialogType === 'edit') {
                // Format the name properly before sending
                const payload = {
                    ...formData,
                    name: formatName(formData.name)
                };
                
                // Handle company ID based on user role
                if (isAdmin) {
                    // Admin can set any company - send the selected company as companyId
                    if (formData.company) {
                        payload.companyId = formData.company;
                    }
                    delete payload.company; // Remove the company field, backend expects companyId
                } else {
                    // Company admin can only edit users in their own company - don't send company
                    delete payload.company;
                }
                
                await apiClient.patch(`/admin/users/${currentUser._id}`, payload);
                setSnackbar({
                    open: true,
                    message: 'User updated successfully',
                    severity: 'success'
                });
            }
            
            // Refresh data and close dialog
            fetchUsers();
            handleCloseDialog();
        } catch (err) {
            console.error('Error:', err);
            setSnackbar({
                open: true,
                message: err.response?.data?.message || 'An error occurred',
                severity: 'error'
            });
        }
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const next = { ...formData, [name]: value };
        if (name === 'role' && (value === 'service_admin' || value === 'service_viewer')) {
            next.personnelType = 'office';
        }
        setFormData(next);
    };

    const handlePersonnelTypeChange = async (user, personnelType, e) => {
        e.stopPropagation();
        try {
            await apiClient.patch(`/admin/users/${user._id}`, { personnelType });
            setUsers((prev) => prev.map((u) => (
                u._id === user._id ? { ...u, personnelType } : u
            )));
            setSnackbar({
                open: true,
                message: 'Personnel type updated',
                severity: 'success'
            });
        } catch (err) {
            console.error('[UserManagement] personnelType update failed', err);
            setSnackbar({
                open: true,
                message: err.response?.data?.message || 'Failed to update personnel type',
                severity: 'error'
            });
        }
    };

    // Handle pagination
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Handle snackbar close
    const handleCloseSnackbar = () => {
        setSnackbar({
            ...snackbar,
            open: false
        });
    };

    // Get role display name and icon
    const getRoleDisplay = (role) => {
        switch (role) {
            case 'admin':
                return {
                    label: 'Admin',
                    color: 'error',
                    icon: <AdminIcon fontSize="small" />
                };
            case 'company_admin':
                return {
                    label: 'Company Admin',
                    color: 'warning',
                    icon: <CompanyAdminIcon fontSize="small" />
                };
            case 'service_admin':
                return {
                    label: 'Service Admin',
                    color: 'success',
                    icon: <BuildIcon fontSize="small" />
                };
            case 'service_viewer':
                return {
                    label: 'Service',
                    color: 'info',
                    icon: <VisibilityIcon fontSize="small" />
                };
            case 'company_user':
                return {
                    label: 'Company User',
                    color: 'primary',
                    icon: <UserIcon fontSize="small" />
                };
            default:
                return {
                    label: role,
                    color: 'default',
                    icon: <UserIcon fontSize="small" />
                };
        }
    };

    const handleOpenResetPasswordDialog = (user, event) => {
        event.stopPropagation();
        setResetPasswordDialog({
            open: true,
            user: user
        });
    };

    const handleCloseResetPasswordDialog = () => {
        setResetPasswordDialog({
            open: false,
            user: null
        });
    };

    const handleSendPasswordReset = async () => {
        try {
            await apiClient.post(`/admin/users/${resetPasswordDialog.user._id}/reset-password`);
            setSnackbar({
                open: true,
                message: 'Password reset email sent successfully',
                severity: 'success'
            });
            handleCloseResetPasswordDialog();
        } catch (err) {
            console.error('Error sending password reset:', err);
            setSnackbar({
                open: true,
                message: err.response?.data?.message || 'Failed to send password reset email',
                severity: 'error'
            });
            handleCloseResetPasswordDialog();
        }
    };

    if (loading) {
        return <TableSkeleton rows={8} showHeader={true} showActions={true} />;
    }

    return (
        <Box sx={{ px: { xs: 2, md: 3 } }}>
            {/* Enhanced Header */}
            <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                justifyContent="space-between" 
                alignItems={{ xs: 'flex-start', sm: 'center' }} 
                spacing={2}
                sx={{ mb: 3 }}
            >
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                        User Management
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage {isAdmin ? 'all system users' : 'company users'} and their access permissions
                    </Typography>
                </Box>
                
                <Stack direction="row" spacing={1}>
                    <Button 
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={() => {
                            console.log('[UserManagement] Force refreshing data...');
                            fetchUsers();
                        }}
                        size={isMobile ? "small" : "medium"}
                    >
                        {isMobile ? '' : 'Refresh'}
                    </Button>
                    <Button 
                        variant="contained" 
                        startIcon={<PersonAddIcon />}
                        onClick={() => handleOpenDialog('add')}
                        size={isMobile ? "small" : "medium"}
                    >
                        Add User
                    </Button>
                </Stack>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    justifyContent="space-between"
                    mb={2}
                    spacing={2}
                >
                    <Typography variant="h6" sx={{ fontWeight: 600, flexShrink: 0 }}>
                        Users ({filteredUsers.length})
                    </Typography>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Search name, email, company, role…"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                    />
                    <Button
                        size="small"
                        startIcon={<FilterListIcon />}
                        endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        onClick={() => setShowFilters(!showFilters)}
                        variant={showFilters ? "contained" : "outlined"}
                        sx={{ flexShrink: 0 }}
                    >
                        Filters
                    </Button>
                </Stack>

                <Collapse in={showFilters}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Filter by Role</InputLabel>
                            <Select
                                value={roleFilter}
                                label="Filter by Role"
                                onChange={(e) => setRoleFilter(e.target.value)}
                            >
                                <MenuItem value="all">All Roles</MenuItem>
                                {isAdmin && <MenuItem value="admin">Admin</MenuItem>}
                                <MenuItem value="company_admin">Company Admin</MenuItem>
                                <MenuItem value="service_admin">Service Admin</MenuItem>
                                <MenuItem value="service_viewer">Service</MenuItem>
                                <MenuItem value="company_user">Company User</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Filter by Status</InputLabel>
                            <Select
                                value={statusFilter}
                                label="Filter by Status"
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <MenuItem value="all">All Status</MenuItem>
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </Collapse>
            </Paper>

            {/* Enhanced Table */}
            <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead sx={{ bgcolor: 'grey.50' }}>
                            <TableRow>
                                {renderSortableHeader('name', 'User')}
                                {renderSortableHeader('role', 'Role')}
                                {renderSortableHeader('personnelType', 'Personnel')}
                                {isAdmin && renderSortableHeader('company', 'Company')}
                                {renderSortableHeader('status', 'Status')}
                                {renderSortableHeader('online', 'Online')}
                                {renderSortableHeader('lastLogin', 'Last Login')}
                                <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedFilteredUsers
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((user) => {
                                    const roleDisplay = getRoleDisplay(user.role);
                                    const isSelf = isAdmin && (currentAuthUser?._id === user._id);
                                    return (
                                        <TableRow 
                                            key={user._id} 
                                            hover
                                            sx={{ 
                                                '&:hover': { bgcolor: 'action.hover' },
                                                cursor: isSelf ? 'default' : 'pointer'
                                            }}
                                            onClick={() => { if (!isSelf) handleOpenDialog('edit', user); }}
                                        >
                                            <TableCell>
                                                <Stack direction="row" spacing={2} alignItems="center">
                                                    <Avatar 
                                                        sx={{ 
                                                            bgcolor: user.isActive ? 'primary.main' : 'grey.400',
                                                            width: 40,
                                                            height: 40
                                                        }}
                                                    >
                                                        {user.name?.charAt(0)?.toUpperCase() || 'U'}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="subtitle2" fontWeight={500}>
                                                            {user.name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {user.email}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={roleDisplay.icon}
                                                    label={roleDisplay.label}
                                                    color={roleDisplay.color}
                                                    size="small"
                                                    sx={{ fontWeight: 600 }}
                                                />
                                            </TableCell>
                                            <TableCell onClick={(e) => e.stopPropagation()}>
                                                <Select
                                                    size="small"
                                                    value={user.personnelType === 'office' ? 'office' : 'driver'}
                                                    onChange={(e) => handlePersonnelTypeChange(user, e.target.value, e)}
                                                    disabled={isSelf}
                                                    sx={{ minWidth: 140, fontSize: 13 }}
                                                >
                                                    <MenuItem value="driver">Driver Personnel</MenuItem>
                                                    <MenuItem value="office">Office Personnel</MenuItem>
                                                </Select>
                                            </TableCell>
                                            {isAdmin && (
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {user.company?.name || (user.role === 'admin' ? '—' : 'No Company')}
                                                    </Typography>
                                                </TableCell>
                                            )}
                                            <TableCell>
                                                <Chip 
                                                    label={user.isActive ? 'Active' : 'Inactive'} 
                                                    color={user.isActive ? 'success' : 'default'} 
                                                    size="small"
                                                    sx={{ fontWeight: 600 }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={user.isOnline ? 'Online' : 'Offline'}
                                                    color={user.isOnline ? 'primary' : 'default'}
                                                    size="small"
                                                    sx={{ fontWeight: 600 }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {user.lastLogin
                                                        ? new Date(user.lastLogin).toLocaleString()
                                                        : 'Never'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                    <Tooltip title={isSelf ? "You cannot edit your own admin account" : "Edit User"}>
                                                        <IconButton 
                                                            size="small"
                                                            disabled={isSelf}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleOpenDialog('edit', user);
                                                            }}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    
                                                    <Tooltip title={isSelf ? "You cannot reset your own password here" : "Reset Password"}>
                                                        <IconButton 
                                                            size="small"
                                                            color="primary"
                                                            disabled={isSelf || !user.isActive}
                                                            onClick={(e) => handleOpenResetPasswordDialog(user, e)}
                                                        >
                                                            <KeyIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    
                                                    <Tooltip title={isSelf ? "You cannot delete your own admin account" : "Delete User"}>
                                                        <IconButton 
                                                            size="small"
                                                            color="error"
                                                            disabled={isSelf}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleOpenDialog('delete', user);
                                                            }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            {sortedFilteredUsers.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={isAdmin ? 8 : 7} align="center" sx={{ py: 8 }}>
                                        <Stack alignItems="center" spacing={2}>
                                            <Avatar sx={{ bgcolor: 'grey.100', width: 64, height: 64 }}>
                                                <UserIcon sx={{ fontSize: 32, color: 'grey.400' }} />
                                            </Avatar>
                                            <Box textAlign="center">
                                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                                    No users found
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {users.length === 0 
                                                        ? "Start by adding your first user." 
                                                        : searchQuery.trim()
                                                            ? "No users match that search."
                                                            : "Try adjusting your filter criteria."
                                                    }
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                
                {sortedFilteredUsers.length > 0 && (
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 20, 25]}
                        component="div"
                        count={sortedFilteredUsers.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                )}
            </Paper>

            {/* Add/Edit User Dialog - Enhanced */}
            <Dialog 
                open={openDialog && (dialogType === 'add' || dialogType === 'edit')} 
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        {dialogType === 'add' ? <PersonAddIcon /> : <EditIcon />}
                        <Typography variant="h6">
                            {dialogType === 'add' ? 'Add New User' : 'Edit User'}
                        </Typography>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                name="name"
                                label="Full Name"
                                fullWidth
                                required
                                value={formData.name}
                                onChange={handleInputChange}
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                name="email"
                                label="Email Address"
                                type="email"
                                fullWidth
                                required
                                value={formData.email}
                                onChange={handleInputChange}
                                variant="outlined"
                            />
                        </Grid>
                        {/* Password field only for adding new users */}
                        {dialogType === 'add' && (
                            <Grid item xs={12}>
                                <Alert severity="info" sx={{ borderRadius: 2 }}>
                                    A password setup link will be sent to the user's email address. They will use this secure link to set their own password.
                                </Alert>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel id="role-select-label">Role</InputLabel>
                                <Select
                                    labelId="role-select-label"
                                    name="role"
                                    value={formData.role}
                                    label="Role"
                                    onChange={handleInputChange}
                                >
                                    {/* Only show Admin role option for admin users */}
                                    {isAdmin && (
                                        <MenuItem value="admin">
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <AdminIcon fontSize="small" />
                                                <span>Admin</span>
                                            </Stack>
                                        </MenuItem>
                                    )}
                                    <MenuItem value="company_admin">
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <CompanyAdminIcon fontSize="small" />
                                            <span>Company Admin</span>
                                        </Stack>
                                    </MenuItem>
                                    <MenuItem value="service_admin">
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <BuildIcon fontSize="small" />
                                            <span>Service Admin</span>
                                        </Stack>
                                    </MenuItem>
                                    <MenuItem value="service_viewer">
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <VisibilityIcon fontSize="small" />
                                            <span>Service</span>
                                        </Stack>
                                    </MenuItem>
                                    <MenuItem value="company_user">
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <UserIcon fontSize="small" />
                                            <span>Company User</span>
                                        </Stack>
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel id="personnel-select-label">Personnel Type</InputLabel>
                                <Select
                                    labelId="personnel-select-label"
                                    name="personnelType"
                                    value={(formData.role === 'service_admin' || formData.role === 'service_viewer') ? 'office' : (formData.personnelType === 'office' ? 'office' : 'driver')}
                                    label="Personnel Type"
                                    disabled={formData.role === 'service_admin' || formData.role === 'service_viewer'}
                                    onChange={handleInputChange}
                                >
                                    <MenuItem value="driver">Driver Personnel</MenuItem>
                                    <MenuItem value="office">Office Personnel</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        {/* Only show company selection for admin users */}
                        {(formData.role !== 'admin' && isAdmin) && (
                            <Grid item xs={12}>
                                <FormControl fullWidth required>
                                    <InputLabel id="company-select-label">Company</InputLabel>
                                    <Select
                                        labelId="company-select-label"
                                        name="company"
                                        value={formData.company}
                                        label="Company"
                                        onChange={handleInputChange}
                                    >
                                        {companies.map(company => (
                                            <MenuItem key={company._id} value={company._id}>
                                                {company.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}
                        {/* Only show status field for edit, not for add */}
                        {dialogType === 'edit' && (
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel id="status-select-label">Status</InputLabel>
                                    <Select
                                        labelId="status-select-label"
                                        name="isActive"
                                        value={formData.isActive}
                                        label="Status"
                                        onChange={handleInputChange}
                                    >
                                        <MenuItem value={true}>Active</MenuItem>
                                        <MenuItem value={false}>Inactive</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}
                        {formData.role !== 'admin' && (
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={Boolean(formData.isVideoAdmin)}
                                            onChange={(e) =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    isVideoAdmin: e.target.checked,
                                                }))
                                            }
                                            name="isVideoAdmin"
                                        />
                                    }
                                    label="Video admin (can upload and publish safety training videos)"
                                />
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 0 }}>
                    <Button onClick={handleCloseDialog} color="inherit">
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSubmit} 
                        variant="contained"
                        disabled={!formData.name || !formData.email}
                        startIcon={dialogType === 'add' ? <PersonAddIcon /> : <EditIcon />}
                    >
                        {dialogType === 'add' ? 'Add User' : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog - Enhanced */}
            <Dialog 
                open={openDialog && dialogType === 'delete'} 
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <DeleteIcon color="error" />
                        <Typography variant="h6">Delete User</Typography>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                        <Typography variant="body2">
                            <strong>Warning:</strong> This action cannot be undone.
                        </Typography>
                    </Alert>
                    <Typography>
                        Are you sure you want to permanently delete user <strong>{currentUser?.name}</strong> ({currentUser?.email})?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 0 }}>
                    <Button onClick={handleCloseDialog} color="inherit">
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSubmit} 
                        color="error" 
                        variant="contained"
                        startIcon={<DeleteIcon />}
                    >
                        Delete User
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Password Reset Confirmation Dialog - Enhanced */}
            <Dialog
                open={resetPasswordDialog.open}
                onClose={handleCloseResetPasswordDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <KeyIcon color="warning" />
                        <Typography variant="h6">Reset Password</Typography>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                        <Typography variant="body2">
                            A password reset link will be sent to the user's email address.
                        </Typography>
                    </Alert>
                    <Typography>
                        Send password reset email to <strong>{resetPasswordDialog.user?.name}</strong> at {resetPasswordDialog.user?.email}?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 0 }}>
                    <Button onClick={handleCloseResetPasswordDialog} color="inherit">
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSendPasswordReset} 
                        variant="contained"
                        color="warning"
                        startIcon={<KeyIcon />}
                    >
                        Send Reset Email
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={snackbar.severity}
                    sx={{ borderRadius: 2 }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default UserManagement; 